import {
  ComponentProps,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { styled } from '@mui/material';
import { PieChart, ResponsiveContainer } from 'recharts';

import { defaults } from './_constants';
import { PieChartProvider, usePieChartContext } from './PieChartContext';
import { PieChartContainerProps, PieChartData } from './types';
import { WithRef } from '../../../../types';
import { mergeSx } from '../../../../utils';

type PieChartContainerRootProps<T extends PieChartData> = Omit<
  PieChartContainerProps<T>,
  'data' | 'radius' | 'variant'
>;

const chartContainerClasses = {
  root: 'PieChartContainer-Root',
  container: 'PieChartContainer-Container',
  body: 'PieChartContainer-Body',
};

// recharts applies inline styles to PieChart (width, height, minWidth, minHeight, maxHeight),
// so its not possible to directly control all of its styles, therefore creating this wrapper
const Root = styled('div', {
  name: 'DsPieChartContainer',
  slot: 'Root',
  target: chartContainerClasses.root,
  overridesResolver: (_, styles) => [styles.root],
})({
  '& svg:focus, & g:focus, & path:focus': { outline: 'none' },
});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  name: 'DsPieChartContainer',
  slot: 'Container',
  target: chartContainerClasses.container,
  overridesResolver: (_, styles) => [styles.container],
})({});

const PieChartContainerRoot = <T extends PieChartData>({
  ref: forwardedRef,
  children,
  margin,
  sx,
  testId = 'pie-chart-container',
  ...rootContainerProps
}: WithRef<HTMLDivElement, PieChartContainerRootProps<T>> &
  ComponentProps<typeof Root>) => {
  const { data, variant, rootWidth, rootHeight, containerWidth } =
    usePieChartContext();

  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => containerRef.current!, []);

  useEffect(() => {
    if (containerRef.current && testId) {
      containerRef.current.setAttribute('data-testid', `${testId}-root`);
    }
  }, [testId]);

  const chartProps = useMemo(
    () => ({
      margin: {
        ...defaults.margin,
        ...margin,
      },
    }),
    [margin]
  );

  return (
    <Root
      sx={mergeSx({ width: rootWidth, height: rootHeight }, sx)}
      data-testid={testId}
      // Forwarding remaining props is needed to allow wrapping with Tooltip
      // as it passes event handlers and data attributes
      {...rootContainerProps}
    >
      <StyledResponsiveContainer
        className={chartContainerClasses.container}
        sx={
          variant === 'half-right'
            ? { transform: 'translateX(-50%)' }
            : undefined
        }
        width={containerWidth}
        ref={containerRef}
      >
        <PieChart
          {...chartProps}
          className={chartContainerClasses.body}
          data={data}
          data-testid={`${testId}-body`}
        >
          {children}
        </PieChart>
      </StyledResponsiveContainer>
    </Root>
  );
};

export const PieChartContainer = <T extends PieChartData>({
  data,
  radius,
  variant = 'full',
  ...props
}: PieChartContainerProps<T>) => {
  return (
    <PieChartProvider data={data} radius={radius} variant={variant}>
      <PieChartContainerRoot {...props} />
    </PieChartProvider>
  );
};
